import { useTranslation } from 'react-i18next';

const fallbackBg = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1600' height='900'%3E%3Crect width='1600' height='900' fill='%23222'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='serif' font-size='48' fill='%23555'%3EKakobuy%3C/text%3E%3C/svg%3E";
const fallbackSneakers = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='500'%3E%3Crect width='400' height='500' fill='%23e8ddd0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='serif' font-size='24' fill='%237a6a5a'%3ESneakers%3C/text%3E%3C/svg%3E";
const fallbackHoodie = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='550'%3E%3Crect width='400' height='550' fill='%23ddd5c7'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='serif' font-size='24' fill='%237a6a5a'%3EHoodie%3C/text%3E%3C/svg%3E";
const fallbackBag = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='500'%3E%3Crect width='400' height='500' fill='%23d9cfc0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='serif' font-size='24' fill='%237a6a5a'%3EBag%3C/text%3E%3C/svg%3E";

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
      {/* Premium full-bleed background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Luxurious%20editorial%20fashion%20flatlay%20with%20silk%20scarves%20gold%20jewelry%20designer%20sunglasses%20and%20leather%20goods%20arranged%20on%20dark%20slate%20marble%20surface%20dramatic%20warm%20side%20lighting%20rich%20terracotta%20and%20cream%20color%20palette%20high-end%20boutique%20aesthetic%20soft%20shadows%20premium%20product%20photography%20no%20text%20no%20logos%20cinematic%20depth%20of%20field%20sophisticated%20European%20style&width=1600&height=900&seq=hero-bg-luxury-v2&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center"
          onError={(e) => { (e.target as HTMLImageElement).src = fallbackBg; }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20"></div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background-50 to-transparent"></div>
      </div>

      <div className="w-full px-4 md:px-6 py-24 md:py-32 relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Left: Text content - white on dark */}
          <div className="flex-1 w-full text-center lg:text-left">
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight drop-shadow-sm">
              {t('hero.h1_line1')}
              <br />
              {t('hero.h1_line2')}
              <br />
              <span className="text-primary-300">{t('hero.h1_line3')}</span>
            </h1>
            <p className="mt-6 text-sm md:text-base text-white/80 leading-relaxed max-w-lg mx-auto lg:mx-0 drop-shadow-sm">
              {t('hero.subtitle')}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
              >
                <span>{t('hero.cta_kakobuy')}</span>
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/15 backdrop-blur-sm text-white text-sm font-semibold hover:bg-white/25 transition-colors cursor-pointer whitespace-nowrap border border-white/20"
              >
                <span>{t('hero.cta_buy')}</span>
                <i className="ri-shopping-bag-line"></i>
              </a>
            </div>

            {/* Trust badges - light */}
            <div className="mt-8 flex flex-wrap gap-4 justify-center lg:justify-start">
              {[
                { icon: 'ri-refresh-line', text: 'Aktualizowane listy produktów' },
                { icon: 'ri-scales-3-line', text: 'Szczegółowe porównania' },
                { icon: 'ri-shield-check-line', text: 'Poradniki bezpieczeństwa' },
                { icon: 'ri-gift-line', text: 'Darmowy dostęp' },
              ].map((badge, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 text-xs text-white/70 whitespace-nowrap drop-shadow-sm"
                >
                  <i className={`${badge.icon} text-primary-300`}></i>
                  {badge.text}
                </span>
              ))}
            </div>
          </div>

          {/* Right: Layered product cards */}
          <div className="flex-1 w-full flex justify-center lg:justify-end">
            <div className="relative w-[300px] h-[360px] md:w-[380px] md:h-[440px]">
              {/* Left card - tilted back */}
              <div className="absolute left-0 bottom-8 w-36 h-48 md:w-44 md:h-56 rounded-2xl overflow-hidden bg-background-50 border border-background-200/60 rotate-[-6deg] z-0 shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Stylish%20white%20leather%20sneakers%20on%20clean%20cream%20minimal%20background%20soft%20natural%20daylight%20editorial%20product%20photography%20elegant%20composition%20warm%20gentle%20shadows%20premium%20fashion%20aesthetic%20no%20text%20no%20logos%20clean%20and%20fresh%20look%20European%20style%20product%20shot%20high%20detail%20texture%20minimalist%20setting&width=400&height=500&seq=hero-card-sneakers-v2&orientation=portrait"
                  alt="Stylish white sneakers - Kakobuy Spreadsheet finds"
                  title="Kakobuy Spreadsheet – Buty sportowe"
                  className="w-full h-full object-cover object-top"
                  onError={(e) => { (e.target as HTMLImageElement).src = fallbackSneakers; }}
                />
              </div>

              {/* Center card - elevated */}
              <div className="absolute left-1/2 top-4 -translate-x-1/2 w-40 h-52 md:w-48 md:h-64 rounded-2xl overflow-hidden bg-background-50 border border-background-200/60 shadow-2xl z-20">
                <img
                  src="https://readdy.ai/api/search-image?query=Premium%20beige%20oversized%20hoodie%20on%20clean%20cream%20background%20soft%20diffused%20natural%20light%20editorial%20fashion%20photography%20elegant%20minimal%20composition%20warm%20gentle%20shadows%20luxury%20streetwear%20aesthetic%20no%20text%20no%20logos%20clean%20fresh%20look%20European%20fashion%20style%20high%20detail%20fabric%20texture%20minimalist%20setting&width=400&height=550&seq=hero-card-hoodie-v2&orientation=portrait"
                  alt="Premium beige hoodie - Kakobuy Spreadsheet fashion"
                  title="Kakobuy Spreadsheet – Bluza premium"
                  className="w-full h-full object-cover object-top"
                  onError={(e) => { (e.target as HTMLImageElement).src = fallbackHoodie; }}
                />
              </div>

              {/* Right card - tilted forward */}
              <div className="absolute right-0 bottom-8 w-36 h-48 md:w-44 md:h-56 rounded-2xl overflow-hidden bg-background-50 border border-background-200/60 rotate-[6deg] z-0 shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20tan%20leather%20crossbody%20bag%20on%20clean%20cream%20minimal%20background%20soft%20natural%20daylight%20editorial%20product%20photography%20refined%20composition%20warm%20gentle%20shadows%20premium%20accessory%20aesthetic%20no%20text%20no%20logos%20clean%20and%20fresh%20look%20European%20style%20product%20shot%20high%20detail%20leather%20texture%20minimalist%20setting&width=400&height=500&seq=hero-card-bag-v2&orientation=portrait"
                  alt="Elegant tan leather bag - Kakobuy Spreadsheet accessories"
                  title="Kakobuy Spreadsheet – Torba skórzana"
                  className="w-full h-full object-cover object-top"
                  onError={(e) => { (e.target as HTMLImageElement).src = fallbackBag; }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}